import { Card, Empty, Spin } from 'antd'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'

import { useInvestigationsServiceGetMonthlyResultStatisticsEndpoint } from 'src/api/queries'
import { useLocales } from 'src/locales'

const LINE_COLORS = [
  '#6C4FB6',
  '#E8893B',
  '#1890ff',
  '#52c41a',
  '#f5222d',
  '#13c2c2',
  '#722ed1',
  '#fa8c16'
]

type ChartRow = {
  label: string
} & Record<string, string | number>

export const CD4Chart = () => {
  const { t, currentLang } = useLocales()

  const { id } = useParams()

  const { data, isLoading } =
    useInvestigationsServiceGetMonthlyResultStatisticsEndpoint(
      {
        personId: id
      },
      [],
      {
        enabled: !!id
      }
    )

  const { chartData, series } = useMemo(() => {
    const months = data?.months ?? []
    const seriesMap = new Map<string, string>()

    const rows: ChartRow[] = months.map((month) => {
      const period = month.period ?? `${month.year}-${month.month}`
      const row: ChartRow = {
        label: dayjs(period).locale(currentLang.value).format('MMM-YY')
      }

      month.testTypes?.forEach((testType) => {
        const id = testType.testType?.id
        if (!id) return

        seriesMap.set(
          id,
          testType.testType?.translation?.[currentLang.value] ?? id
        )
        row[id] = testType.total ?? 0
      })

      return row
    })

    return {
      chartData: rows,
      series: Array.from(seriesMap, ([id, name]) => ({ id, name }))
    }
  }, [data, currentLang.value])

  return (
    <Card
      styles={{
        body: {
          padding: 16
        }
      }}
      className="w-full"
    >
      {isLoading ? (
        <Spin />
      ) : chartData.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="label"
              label={{
                value: t('charts.months'),
                position: 'insideBottom',
                offset: -5
              }}
            />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend />
            {series.map(({ id, name }, index) => (
              <Line
                key={id}
                type="monotone"
                dataKey={id}
                name={name}
                connectNulls
                stroke={LINE_COLORS[index % LINE_COLORS.length]}
                strokeWidth={2}
                dot={{
                  fill: LINE_COLORS[index % LINE_COLORS.length],
                  strokeWidth: 2,
                  r: 4
                }}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </Card>
  )
}
